"use client";

/**
 * RegioBlock — "In uw buurt" met illustratie-kaart en projecten-teller.
 *
 * Port van Claude Design "Regio Blok.html" (7 varianten). De {{city}}
 * placeholder wordt door MainPage's applyCityToBlock vervangen met de
 * pagina-specifieke plaatsnaam (afgeleid uit display_name of subdomain).
 *
 * Projecten-aantal schaalt automatisch op basis van inwonertal:
 *   Amsterdam (882k inwoners) = 2116 projecten als baseline
 *   rate = 2116 / 882000 ≈ 0.0024 projecten/inwoner
 *   andere plaatsen: population × rate, afgerond op honderdtal
 *
 * Varianten: classic, map-forward, dark, asymmetric, postcard, number-hero, journey.
 */

import { useState } from "react";

export type RegioVariant = "classic" | "map-forward" | "dark" | "asymmetric";

export interface RegioBullet {
  title: string;
  body: string;
}

export interface RegioReview {
  quote: string;
  name: string;
  city: string;
  rating?: number;
}

export interface RegioContent {
  city?: string;
  /**
   * Bron van `city`:
   *  - "page": vaste stadspagina → "Wij kennen Amsterdam." (confident)
   *  - "geo":  IP-detected → "Wij kennen Amsterdam en omstreken" (zacht)
   *  - "none": geen city → "Wij kennen uw regio" (algemeen)
   * Default = "page" voor backwards compat met bestaande city-pages.
   */
  city_source?: "page" | "geo" | "none";
  /**
   * Confidence in geo-accuratesse. "low" = mobiel-provider-exit gedetecteerd
   * → toon "Niet uw stad? Pas aan →" link zodat bezoeker zelf kan corrigeren.
   * "high" = desktop of mobiel met onverdachte exit-stad.
   */
  confidence?: "high" | "low";
  eyebrow?: string;
  headline?: string;
  body?: string;
  projects?: number;
  region_name?: string;
  bullets?: RegioBullet[];
  review?: RegioReview;
  cta_text?: string;
  cta_href?: string;
  nearby_cities?: string[];
}

interface Props {
  content: RegioContent;
  variant?: RegioVariant;
}

const T = {
  cream: "var(--cream, #EAE4D7)",
  creamDeep: "var(--cream-dark, #DDD4C0)",
  ink: "var(--ink, #1C1C1A)",
  inkSoft: "#3A3A36",
  muted: "var(--stone, #6E6A60)",
  line: "#D8D1BE",
  cta: "var(--terracotta, #FF6B1A)",
  ctaSoft: "var(--terracotta-light, #FF9359)",
  white: "#FDFBF5",
  darkBg: "#1F2520",
};

// ── Populatie-tabel voor project-berekening ─────────────────────────────
// Bron: CBS 2023/2024. Rate: 2116 / 882000 ≈ 0.0024 projecten/inwoner.
const POPULATION: Record<string, number> = {
  amsterdam: 882000,
  rotterdam: 664000,
  "den haag": 569000,
  utrecht: 374000,
  eindhoven: 246000,
  groningen: 239000,
  tilburg: 226000,
  almere: 220000,
  breda: 185000,
  nijmegen: 185000,
  apeldoorn: 166000,
  haarlem: 163000,
  arnhem: 164000,
  enschede: 160000,
  amersfoort: 162000,
  "s-hertogenbosch": 160000,
  "'s-hertogenbosch": 160000,
  zwolle: 132000,
  zaanstad: 164000,
  leeuwarden: 126000,
  leiden: 127000,
  maastricht: 121000,
  dordrecht: 120000,
  ede: 118000,
  alphen: 113000,
  "alphen aan den rijn": 113000,
  haarlemmermeer: 160000,
  alkmaar: 111000,
  emmen: 107000,
  venlo: 103000,
  delft: 107000,
  deventer: 103000,
  westland: 112000,
  sittard: 93000,
  "sittard-geleen": 93000,
  helmond: 94000,
  amstelveen: 93000,
  hilversum: 91000,
  hengelo: 83000,
  oss: 93000,
  purmerend: 93000,
  roosendaal: 78000,
  schiedam: 80000,
  lelystad: 81000,
  spijkenisse: 73000,
  hoorn: 75000,
  velsen: 69000,
  gouda: 76000,
  almelo: 74000,
  assen: 69000,
  vlaardingen: 76000,
  nissewaard: 85000,
  heerlen: 86000,
  capelle: 66000,
  "capelle aan den ijssel": 66000,
};

const BASELINE_POP = 882000;
const BASELINE_PROJECTS = 2116;
const RATE = BASELINE_PROJECTS / BASELINE_POP;

export function projectsForCity(city: string | undefined): number {
  if (!city) return BASELINE_PROJECTS;
  const key = city.trim().toLowerCase();
  const pop = POPULATION[key];
  if (!pop) {
    // Onbekende plaats: gebruik 50k inwoners als neutrale default
    return Math.max(50, Math.round((50000 * RATE) / 10) * 10);
  }
  // Rond af op tiental voor leesbare cijfers
  return Math.round((pop * RATE) / 10) * 10;
}

const DEFAULT_BULLETS: RegioBullet[] = [
  { title: "Vaste monteurs uit de regio", body: "Geen onbekende gezichten — onze teams komen regelmatig in uw omgeving." },
  { title: "Gratis thuisadvies binnen 5 werkdagen", body: "In heel de regio komt onze adviseur bij u langs." },
  { title: "Binnen 4 weken leverbaar", body: "Van offerte tot installatie — geen lange wachttijden." },
];

const DEFAULT_REVIEW: RegioReview = {
  quote: "Monteurs waren prettig in de omgang en werkten netjes. Binnen drie weken een volledig nieuwe badkamer.",
  name: "Familie van der Berg",
  city: "Amsterdam-Zuid",
  rating: 5,
};

function unwrapBullets(raw: unknown, fallback: RegioBullet[]): RegioBullet[] {
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const out = raw
    .map((i) => {
      if (!i || typeof i !== "object") return null;
      const o = i as { title?: unknown; body?: unknown };
      if (typeof o.title !== "string") return null;
      return { title: o.title, body: typeof o.body === "string" ? o.body : "" };
    })
    .filter((b): b is RegioBullet => b !== null);
  return out.length > 0 ? out : fallback;
}

function resolve(content: RegioContent) {
  const trimmedCity = content.city?.trim() ?? "";
  const source: "page" | "geo" | "none" = content.city_source ?? (trimmedCity ? "page" : "none");
  // Bij low confidence (mobiel + provider-exit-stad) tonen we GEEN specifieke
  // stad — beter algemene "uw regio" copy dan een onbetrouwbare claim.
  // De gedetecteerde city wordt wel meegegeven aan de "Pas aan" link
  // ("Niet Amsterdam? Pas aan") zodat bezoekers weten waarom 't generiek is.
  const isLowConfidence = source === "geo" && content.confidence === "low";
  const detectedCity = trimmedCity; // wat server heeft gedetecteerd
  // city = wat we ACTUEEL in copy tonen — leeg bij low-confidence of none.
  const city = (isLowConfidence || source === "none") ? "" : trimmedCity;
  const hasCity = city.length > 0;
  const showCityCorrectLink = isLowConfidence && detectedCity.length > 0;

  // Review-stad lokaliseren — alleen tonen als geo daadwerkelijk een stad
  // heeft gevonden. Bij missende geo-detectie laten we de city leeg zodat
  // het review-attribuut alleen de naam toont (geen rare 'Nederland'
  // generieke fallback meer, voelt nep).
  const resolvedReview: RegioReview = content.review
    ? { ...content.review }
    : { ...DEFAULT_REVIEW, city: hasCity ? city : "" };
  if (
    !content.review ||
    (resolvedReview.city &&
      hasCity &&
      !resolvedReview.city.toLowerCase().includes(city.toLowerCase()))
  ) {
    resolvedReview.city = hasCity ? city : "";
  }

  // city_label = wat we tonen in copy. "geo" voegt "en omstreken" toe.
  const city_label =
    source === "page" ? city
    : source === "geo" && hasCity ? `${city} en omstreken`
    : "uw regio";

  return {
    city,
    detectedCity,
    source,
    hasCity,
    showCityCorrectLink,
    city_label,
    projects: typeof content.projects === "number" && content.projects > 0
      ? content.projects
      : hasCity ? projectsForCity(city) : BASELINE_PROJECTS,
    eyebrow: content.eyebrow ?? "In uw buurt",
    headline: content.headline,
    body: content.body,
    bullets: unwrapBullets(content.bullets, DEFAULT_BULLETS),
    review: resolvedReview,
    cta_text: content.cta_text ?? "Vraag uw brochure aan",
    cta_href: content.cta_href ?? "#brochure-form",
    region_name: content.region_name,
    nearby_cities: Array.isArray(content.nearby_cities) && content.nearby_cities.length > 0
      ? content.nearby_cities
      : ["Amstelveen", "Diemen", "Haarlem", "Zaandam", "Hoofddorp", "Purmerend"],
  };
}

// ── Illustrated map SVG — gedeeld tussen varianten ──────────────────────
const DEFAULT_DOTS = [
  { x: 520, y: 245, label: "Amsterdam" },
  { x: 420, y: 180, label: "Haarlem" },
  { x: 600, y: 310, label: "Utrecht" },
  { x: 680, y: 220, label: "Almere" },
  { x: 330, y: 340, label: "Den Haag" },
  { x: 390, y: 400, label: "Rotterdam" },
  { x: 560, y: 130, label: "Hoorn" },
  { x: 450, y: 450, label: "Gouda" },
  { x: 720, y: 400, label: "Amersfoort" },
  { x: 280, y: 240, label: "Leiden" },
];

function IllustratedMap({
  activeCity,
  theme = "light",
  showRings = true,
  showGrid = true,
  interactive = true,
}: {
  activeCity: string;
  theme?: "light" | "sage" | "cream-dark";
  showRings?: boolean;
  showGrid?: boolean;
  interactive?: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const palette = {
    light: { bg: "#F4EFE1", grid: "#E3DBC6", path: "#CFC8B4", dot: "#A9B89E", dotStroke: "#8B9B7E", chipBg: T.white },
    sage: { bg: "#E5E8DC", grid: "#D2D8C5", path: "#B9C2A8", dot: "#8B9B7E", dotStroke: "#6E8060", chipBg: T.white },
    "cream-dark": { bg: "#DDD4C0", grid: "#C9BFA6", path: "#B0A88F", dot: "#8B9B7E", dotStroke: "#6E8060", chipBg: T.white },
  }[theme];

  // Vind de actieve city in DEFAULT_DOTS (case-insensitive). Als niet in de lijst,
  // of als activeCity leeg is (geo-detect mislukt), tonen we geen actief
  // middelpunt — alle dots blijven gelijkwaardig zichtbaar.
  const activeLower = activeCity.trim().toLowerCase();
  const showActive = activeLower.length > 0;
  const activeDot = showActive
    ? (DEFAULT_DOTS.find((d) => d.label.toLowerCase() === activeLower) ?? DEFAULT_DOTS[0]!)
    : null;
  const center = activeDot ? { x: activeDot.x, y: activeDot.y } : null;

  return (
    <svg viewBox="0 0 800 560" style={{ width: "100%", height: "100%", display: "block" }}>
      <rect x="0" y="0" width="800" height="560" fill={palette.bg} />
      {showGrid ? (
        <g stroke={palette.grid} strokeWidth="1" opacity="0.8">
          {Array.from({ length: 14 }).map((_, i) => (
            <line key={`v${i}`} x1={i * 60} y1="0" x2={i * 60} y2="560" />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 60} x2="800" y2={i * 60} />
          ))}
        </g>
      ) : null}
      <g stroke={palette.path} strokeWidth="2" fill="none" strokeLinecap="round">
        <path d="M -20 180 Q 180 140 340 210 T 680 190 Q 780 180 840 220" opacity="0.85" />
        <path d="M -20 340 Q 160 310 280 370 T 560 360 Q 700 350 840 380" opacity="0.7" />
        <path d="M 120 -20 Q 180 180 320 280 T 480 520 Q 520 580 560 620" opacity="0.55" />
        <path d="M 680 -20 Q 640 140 560 260 T 500 500" opacity="0.45" />
      </g>
      <g>
        {DEFAULT_DOTS.map((d, i) => {
          if (d.label.toLowerCase() === activeLower) return null;
          const isHover = hover === i;
          return (
            <g
              key={i}
              onMouseEnter={() => interactive && setHover(i)}
              onMouseLeave={() => interactive && setHover(null)}
              style={{ cursor: interactive ? "pointer" : "default" }}
            >
              <circle cx={d.x} cy={d.y} r={isHover ? 9 : 7} fill={palette.dot} stroke={palette.dotStroke} strokeWidth="1.5" style={{ transition: "r 0.2s" }} />
              {isHover ? (
                <g>
                  <rect x={d.x + 14} y={d.y - 14} width={d.label.length * 7.5 + 20} height="26" rx="13" fill={palette.chipBg} stroke={palette.grid} strokeWidth="1" />
                  <text x={d.x + 24} y={d.y + 4} fontSize="13" fontFamily="system-ui, sans-serif" fontWeight="500" fill={T.ink}>
                    {d.label}
                  </text>
                </g>
              ) : null}
            </g>
          );
        })}
      </g>
      {showActive && center ? (
        <>
          {showRings ? (
            <g>
              <circle cx={center.x} cy={center.y} r="70" fill={T.cta} opacity="0.08" />
              <circle cx={center.x} cy={center.y} r="45" fill={T.cta} opacity="0.14" />
              <circle cx={center.x} cy={center.y} r="24" fill={T.cta} opacity="0.22" />
            </g>
          ) : null}
          <circle cx={center.x} cy={center.y} r="9" fill={T.cta} />
          <circle cx={center.x} cy={center.y} r="3.5" fill={T.white} />
          <g transform={`translate(${Math.max(20, center.x - 200)}, ${Math.max(20, center.y - 140)})`}>
            <rect x="0" y="0" width={activeCity.length * 9 + 50} height="40" rx="20" fill={palette.chipBg} stroke={palette.grid} strokeWidth="1" />
            <circle cx="22" cy="20" r="5" fill={T.cta} />
            <text x="36" y="25" fontSize="15" fontFamily="system-ui, sans-serif" fontWeight="600" fill={T.ink}>
              {activeCity}
            </text>
          </g>
        </>
      ) : null}
    </svg>
  );
}

// ── Gedeelde building blocks ────────────────────────────────────────────
function Eyebrow({ children, color = T.muted }: { children: React.ReactNode; color?: string }) {
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: "0.16em", textTransform: "uppercase", color, marginBottom: 20 }}>
      {children}
    </div>
  );
}

function Bullets({ items, textColor = T.ink, subColor = T.muted, checkColor = T.cta }: { items: RegioBullet[]; textColor?: string; subColor?: string; checkColor?: string }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "28px 0 0", display: "grid", gap: 18 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 14 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, marginTop: 3 }}>
            <path d="M3.5 9.5l3.5 3.5 8-8" stroke={checkColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 15, fontWeight: 600, color: textColor, lineHeight: 1.35 }}>{it.title}</div>
            <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 13.5, lineHeight: 1.55, color: subColor, marginTop: 4 }}>{it.body}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Stars({ n = 5, color = T.cta, size = 14 }: { n?: number; color?: string; size?: number }) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {Array.from({ length: n }).map((_, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 14 14" fill={color}>
          <path d="M7 1l1.8 3.9 4.2.5-3.1 2.9.8 4.2L7 10.5l-3.7 2 .8-4.2L1 5.4l4.2-.5L7 1z" />
        </svg>
      ))}
    </div>
  );
}

function Cta({ text, href }: { text: string; href: string }) {
  return (
    <a
      href={href}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 28, background: T.cta, color: "#FFF", textDecoration: "none", padding: "15px 26px", borderRadius: 999, fontSize: 15.5, fontWeight: 600, boxShadow: "0 10px 26px -10px rgba(0,0,0,0.3)" }}
    >
      {text}
      <span style={{ fontSize: 18 }}>→</span>
    </a>
  );
}

/**
 * Subtiele "Niet uw stad? Pas aan →" link — alleen tonen bij low confidence
 * (mobiel + provider-exit stad). Triggert global event dat CityPickerModal
 * oppikt zodat bezoeker zelf zijn postcode kan invoeren.
 */
function CityCorrectLink({ city }: { city: string }) {
  return (
    <button
      type="button"
      data-open-city-picker
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        marginTop: 12,
        padding: "6px 0",
        background: "transparent",
        border: "none",
        color: T.muted,
        fontSize: 13,
        fontWeight: 500,
        cursor: "pointer",
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textUnderlineOffset: 4,
      }}
      aria-label={`Niet ${city}? Klik om uw eigen stad in te voeren`}
    >
      Niet {city}? Pas uw locatie aan →
    </button>
  );
}

// ── Variant 1: classic ──────────────────────────────────────────────────
function Classic({ content }: { content: RegioContent }) {
  const c = resolve(content);
  return (
    <section style={{ background: T.cream, padding: "96px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .rg-classic { grid-template-columns: 1fr !important; gap: 36px !important; padding: 0 !important; } .rg-classic h2 { font-size: clamp(28px, 6vw, 44px) !important; } }`}</style>
      <div className="rg-classic" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 80, alignItems: "center" }}>
        <div>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 400, fontSize: 56, lineHeight: 1.05, letterSpacing: "-0.02em", color: T.ink, margin: 0 }}>
            {c.headline ?? (
              c.hasCity ? (
                <>
                  Projecten in {c.city}
                  <br />en omstreken
                </>
              ) : (
                <>Projecten in<br />uw regio</>
              )
            )}
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: T.inkSoft, margin: "24px 0 0", maxWidth: 480 }}>
            {c.body ?? (
              c.hasCity
                ? `Onze vaste monteurs zijn regelmatig in ${c.city_label}. Dat betekent korte lijnen, snelle planning en een team dat de wegen, wijken en typische woningvoorraad van de regio goed kent.`
                : `Onze vaste monteurs zijn regelmatig in uw regio. Dat betekent korte lijnen, snelle planning en een team dat de wegen, wijken en typische woningvoorraad goed kent.`
            )}
          </p>
          {c.showCityCorrectLink ? <CityCorrectLink city={c.detectedCity} /> : null}
          <Bullets items={c.bullets} />
        </div>
        <div style={{ position: "relative", aspectRatio: "1.4 / 1", borderRadius: 24, overflow: "hidden", border: `1px solid ${T.line}`, boxShadow: "0 20px 60px -30px rgba(28,28,26,0.2)" }}>
          <IllustratedMap activeCity={c.city} theme="light" />
          <div style={{ position: "absolute", right: 20, bottom: 20, background: T.white, borderRadius: 14, padding: "14px 18px", border: `1px solid ${T.line}`, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 500, color: T.ink, lineHeight: 1 }}>{c.projects.toLocaleString("nl-NL")}+ projecten</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>in deze regio</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 2: map-forward with review ──────────────────────────────────
function MapForward({ content }: { content: RegioContent }) {
  const c = resolve(content);
  return (
    <section style={{ background: T.cream, padding: "96px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){
        .rg-mf { grid-template-columns: 1fr !important; gap: 36px !important; }
        .rg-mf-map { min-height: 340px !important; order: 1 !important; }
        .rg-mf-text { order: 0 !important; }
      }`}</style>
      <div className="rg-mf" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 72, alignItems: "stretch" }}>
        <div className="rg-mf-map" style={{ position: "relative", borderRadius: 24, overflow: "hidden", border: `1px solid ${T.line}`, boxShadow: "0 20px 60px -30px rgba(28,28,26,0.2)", minHeight: 560 }}>
          <IllustratedMap activeCity={c.city} theme="sage" />
          <div style={{ position: "absolute", right: 24, bottom: 24, background: T.white, borderRadius: 14, padding: "16px 20px", border: `1px solid ${T.line}` }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 500, color: T.ink, lineHeight: 1 }}>{c.projects.toLocaleString("nl-NL")}+</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>projecten in deze regio</div>
          </div>
        </div>
        <div className="rg-mf-text" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <Eyebrow>{c.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 400, fontSize: 52, lineHeight: 1.05, letterSpacing: "-0.02em", color: T.ink, margin: 0 }}>
              {c.headline ?? (
                c.hasCity ? (
                  <>
                    Wij kennen
                    <br />
                    {c.city_label}.
                  </>
                ) : (
                  <>
                    Wij kennen
                    <br />
                    uw regio.
                  </>
                )
              )}
            </h2>
            <p style={{ fontSize: 17, lineHeight: 1.55, color: T.inkSoft, margin: "24px 0 0", maxWidth: 480 }}>
              {c.body ?? `Onze vaste teams rijden elke week door de regio. Van grachtenpand tot nieuwbouwflat — we kennen de wooncontext en plannen altijd een monteur in die de weg weet.`}
            </p>
            {c.showCityCorrectLink ? <CityCorrectLink city={c.detectedCity} /> : null}
            <Bullets items={c.bullets} />
          </div>
          <div style={{ background: T.white, borderRadius: 18, padding: 24, border: `1px solid ${T.line}`, marginTop: 40 }}>
            <Stars />
            <p style={{ fontFamily: "'Fraunces', serif", fontSize: 19, lineHeight: 1.4, color: T.ink, margin: "14px 0", fontStyle: "italic" }}>
              &ldquo;{c.review.quote}&rdquo;
            </p>
            <div style={{ fontSize: 13, color: T.muted }}>
              {c.review.name}{c.review.city ? ` · ${c.review.city}` : ""}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 3: dark ─────────────────────────────────────────────────────
function Dark({ content }: { content: RegioContent }) {
  const c = resolve(content);
  return (
    <section style={{ background: T.darkBg, padding: "96px 28px", fontFamily: "system-ui, sans-serif", color: T.cream }}>
      <style>{`@media (max-width: 1024px){ .rg-dark { grid-template-columns: 1fr !important; gap: 36px !important; } }`}</style>
      <div className="rg-dark" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 80, alignItems: "center" }}>
        <div>
          <Eyebrow color="#8A9280">{c.eyebrow}</Eyebrow>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 400, fontSize: 56, lineHeight: 1.05, letterSpacing: "-0.02em", color: T.cream, margin: 0, fontStyle: "italic" }}>
            {c.headline ?? (
              c.hasCity ? (
                <>
                  Bij u in
                  <br />
                  {c.city_label}.
                </>
              ) : (
                <>
                  Door heel
                  <br />
                  Nederland.
                </>
              )
            )}
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: "#C9C3B0", margin: "24px 0 0", maxWidth: 480 }}>
            {c.body ?? `Een eigen team in de regio maakt het verschil. Korte reistijd, bekende gezichten, en kennis van de lokale woningvoorraad — van jaren-30 woningen tot nieuwbouwappartementen.`}
          </p>
          {c.showCityCorrectLink ? <CityCorrectLink city={c.detectedCity} /> : null}
          <Bullets items={c.bullets} textColor={T.cream} subColor="#9A9684" checkColor={T.ctaSoft} />
          <div style={{ marginTop: 48, paddingTop: 32, borderTop: "1px solid #3A4036", display: "flex", gap: 20, alignItems: "flex-start" }}>
            <Stars color={T.ctaSoft} />
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: "'Fraunces', serif", fontSize: 17, lineHeight: 1.5, color: T.cream, margin: 0, fontStyle: "italic" }}>
                &ldquo;{c.review.quote}&rdquo;
              </p>
              <div style={{ fontSize: 13, color: "#8A9280", marginTop: 10 }}>
                — {c.review.name}{c.review.city ? `, ${c.review.city}` : ""}
              </div>
            </div>
          </div>
        </div>
        <div style={{ position: "relative", aspectRatio: "1.3 / 1", borderRadius: 24, overflow: "hidden", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.5)" }}>
          <IllustratedMap activeCity={c.city} theme="cream-dark" />
          <div style={{ position: "absolute", right: 20, bottom: 20, background: T.darkBg, borderRadius: 14, padding: "14px 18px", border: "1px solid #3A4036" }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 500, color: T.cream, lineHeight: 1 }}>
              {c.projects.toLocaleString("nl-NL")}+ projecten
            </div>
            <div style={{ fontSize: 12, color: "#9A9684", marginTop: 4 }}>in deze regio</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 4: asymmetric ──────────────────────────────────────────────
function Asymmetric({ content }: { content: RegioContent }) {
  const c = resolve(content);
  return (
    <section style={{ background: T.cream, fontFamily: "system-ui, sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{`@media (max-width: 1024px){ .rg-asym { grid-template-columns: 1fr !important; min-height: auto !important; } .rg-asym > :first-child { padding: 40px 24px 24px !important; } .rg-asym-review { left: 24px !important; bottom: 24px !important; position: relative !important; margin-top: 20px; } }`}</style>
      <div className="rg-asym" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", minHeight: 640 }}>
        <div style={{ padding: "96px 48px 96px 60px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 400, fontSize: 60, lineHeight: 1.05, letterSpacing: "-0.02em", color: T.ink, margin: 0 }}>
            {c.headline ?? (
              <>
                Een team dat
                <br />
                de <span style={{ fontStyle: "italic", color: T.cta }}>regio kent.</span>
              </>
            )}
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: T.inkSoft, margin: "24px 0 0", maxWidth: 480 }}>
            {c.body ?? (
              c.hasCity
                ? `Onze monteurs wonen en werken in en rond ${c.city_label}. Geen onderaannemers, geen wisselende gezichten — gewoon een vast team dat de buurt kent.`
                : `Onze monteurs wonen en werken door heel Nederland. Geen onderaannemers, geen wisselende gezichten — gewoon een vast team dat de buurt kent.`
            )}
          </p>
          {c.showCityCorrectLink ? <CityCorrectLink city={c.detectedCity} /> : null}
          <Bullets items={c.bullets} />
        </div>
        <div style={{ position: "relative" }}>
          <IllustratedMap activeCity={c.city} theme="sage" />
          <div style={{ position: "absolute", top: 40, right: 40, background: T.white, borderRadius: 18, padding: "20px 24px", border: `1px solid ${T.line}`, boxShadow: "0 12px 32px rgba(0,0,0,0.08)" }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 40, fontWeight: 500, color: T.cta, lineHeight: 1 }}>{c.projects.toLocaleString("nl-NL")}+</div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>
              projecten in
              <br />
              deze regio
            </div>
          </div>
          <div className="rg-asym-review" style={{ position: "absolute", left: -40, bottom: 40, background: T.white, borderRadius: 18, padding: 24, maxWidth: 340, border: `1px solid ${T.line}`, boxShadow: "0 16px 40px rgba(0,0,0,0.1)" }}>
            <Stars />
            <p style={{ fontFamily: "'Fraunces', serif", fontSize: 17, lineHeight: 1.45, color: T.ink, margin: "12px 0", fontStyle: "italic" }}>
              &ldquo;{c.review.quote}&rdquo;
            </p>
            <div style={{ fontSize: 13, color: T.muted }}>
              {c.review.name}{c.review.city ? ` · ${c.review.city}` : ""}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function RegioBlock({ content, variant = "classic" }: Props) {
  switch (variant) {
    case "map-forward":
      return <MapForward content={content} />;
    case "dark":
      return <Dark content={content} />;
    case "asymmetric":
      return <Asymmetric content={content} />;
    case "classic":
    default:
      return <Classic content={content} />;
  }
}

// ── Ongebruikte varianten (laten staan voor later reactiveren) ─────────
function _Postcard({ content }: { content: RegioContent }) {
  const c = resolve(content);
  return (
    <section style={{ background: T.cream, padding: "56px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 800px){ .rg-pc { padding: 28px 22px 26px !important; transform: rotate(-0.3deg) !important; } .rg-pc h2 { font-size: 30px !important; max-width: 80% !important; } .rg-pc-body { max-width: 100% !important; font-size: 14px !important; } .rg-pc-stamp { width: 72px !important; height: 82px !important; } .rg-pc-post { width: 72px !important; height: 72px !important; top: 50px !important; right: 46px !important; } }`}</style>
      <div className="rg-pc" style={{ maxWidth: 900, margin: "0 auto", background: "#FCF8EE", border: "1px dashed #C9BFA6", borderRadius: 4, position: "relative", boxShadow: "0 18px 40px -20px rgba(0,0,0,0.22), 0 1px 0 rgba(255,255,255,0.6) inset", padding: "44px 52px 42px", transform: "rotate(-0.5deg)" }}>
        <div className="rg-pc-stamp" style={{ position: "absolute", top: 28, right: 32, width: 92, height: 108, background: "#FFFFFF", border: "1px solid #D8D1BE", padding: 4, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", transform: "rotate(3deg)" }}>
          <div style={{ width: "100%", height: "100%", background: `linear-gradient(135deg, ${T.cta} 0%, ${T.ctaSoft} 100%)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#FFF", padding: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", opacity: 0.9 }}>LOKAAL</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 500, lineHeight: 1 }}>{c.projects.toLocaleString("nl-NL")}</div>
            <div style={{ fontSize: 9, fontWeight: 600, opacity: 0.85, marginTop: 2 }}>projecten</div>
          </div>
        </div>
        <div className="rg-pc-post" style={{ position: "absolute", top: 80, right: 78, width: 96, height: 96, borderRadius: "50%", border: `1.5px dashed ${T.inkSoft}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.inkSoft, transform: "rotate(-14deg)", pointerEvents: "none", opacity: 0.65 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.14em" }}>• {c.city.toUpperCase()} •</div>
          <div style={{ fontSize: 8, fontWeight: 600, letterSpacing: "0.08em", marginTop: 2 }}>DEZE MAAND</div>
          <div style={{ width: 40, height: 1, background: T.inkSoft, margin: "4px 0", opacity: 0.5 }} />
          <div style={{ fontSize: 8, fontWeight: 600, letterSpacing: "0.08em" }}>NEDERLAND</div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: T.cta, marginBottom: 10, textTransform: "uppercase" }}>Groeten uit {c.city}</div>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic", fontWeight: 400, fontSize: 48, lineHeight: 1.05, color: T.ink, margin: "0 0 18px", maxWidth: "62%", letterSpacing: "-0.01em" }}>
          Lieve {c.city}, <br /> wij zijn er ook
          <br />
          <span style={{ fontStyle: "normal", fontWeight: 500 }}>voor u.</span>
        </h2>
        <div
          className="rg-pc-body"
          style={{
            maxWidth: "60%",
            fontSize: 16,
            color: T.inkSoft,
            lineHeight: 1.7,
            marginBottom: 26,
            backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent 27px, #E8E0CC 27px, #E8E0CC 28px)`,
            paddingBottom: 2,
          }}
        >
          Met {c.projects.toLocaleString("nl-NL")} badkamers achter de rug kennen we de
          <br />
          huizen in uw regio door en door. Rijtjeshuis uit de
          <br />
          jaren &lsquo;30 of appartement — wij weten de weg.
          <br />
          <span style={{ color: T.cta, fontWeight: 600 }}>Geen onderaannemers. Eigen mensen. Altijd.</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 2 }}>Hartelijke groet,</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 26, color: T.ink }}>— Het team</div>
          </div>
          <div style={{ flex: 1, minWidth: 120 }} />
          <a href={c.cta_href} style={{ background: T.cta, color: "#FFF", textDecoration: "none", padding: "14px 22px", borderRadius: 999, fontSize: 15, fontWeight: 600, letterSpacing: "0.01em", boxShadow: "0 8px 20px -8px rgba(0,0,0,0.3)", display: "inline-flex", alignItems: "center", gap: 8 }}>
            {c.cta_text}
            <span style={{ fontSize: 16 }}>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function _NumberHero({ content }: { content: RegioContent }) {
  const c = resolve(content);
  return (
    <section style={{ width: "100%", background: T.cream, padding: "64px 28px", fontFamily: "system-ui, sans-serif", position: "relative", overflow: "hidden" }}>
      <div aria-hidden style={{ position: "absolute", inset: 0, opacity: 0.12, pointerEvents: "none" }}>
        <IllustratedMap theme="light" activeCity={c.city} interactive={false} showRings={false} />
      </div>
      <style>{`@media (max-width: 1024px){ .rg-nh { grid-template-columns: 1fr !important; gap: 28px !important; } .rg-nh-big { font-size: 120px !important; } }`}</style>
      <div className="rg-nh" style={{ position: "relative", maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 60, alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: T.cta, marginBottom: 12, textTransform: "uppercase" }}>Al actief in uw buurt</div>
          <div style={{ position: "relative", display: "inline-block" }}>
            <div className="rg-nh-big" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 220, lineHeight: 0.9, color: T.ink, letterSpacing: "-0.04em" }}>
              {c.projects.toLocaleString("nl-NL")}
            </div>
            <div style={{ position: "absolute", top: -14, right: -34, background: T.cta, color: "#FFF", padding: "10px 20px", borderRadius: 999, fontSize: 15, fontWeight: 700, letterSpacing: "0.02em", boxShadow: "0 10px 24px -10px rgba(0,0,0,0.3), 0 0 0 4px rgba(255,255,255,0.8)", transform: "rotate(6deg)", whiteSpace: "nowrap" }}>
              📍 {c.city}
            </div>
          </div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 30, fontWeight: 500, color: T.ink, marginTop: 12, lineHeight: 1.15 }}>badkamers geïnstalleerd in uw regio</div>
          <div style={{ marginTop: 22, fontSize: 16, color: T.inkSoft, lineHeight: 1.6, maxWidth: 460 }}>Uw straat, uw postcode, uw huis-type — de kans is groot dat onze monteurs er al zijn geweest.</div>
          <a href={c.cta_href} style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 28, background: T.cta, color: "#FFF", textDecoration: "none", padding: "15px 26px", borderRadius: 999, fontSize: 16, fontWeight: 600, boxShadow: "0 10px 26px -10px rgba(0,0,0,0.3)" }}>
            Gratis brochure voor {c.city}
            <span style={{ fontSize: 18 }}>→</span>
          </a>
        </div>
        <div>
          <div style={{ background: T.white, border: `1px solid ${T.line}`, borderRadius: 18, padding: 28, boxShadow: "0 20px 40px -24px rgba(0,0,0,0.15)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", color: T.muted, marginBottom: 14, textTransform: "uppercase" }}>Werkgebied rond {c.city}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 22 }}>
              {c.nearby_cities.map((n, i) => (
                <div key={i} style={{ background: i === 0 ? T.cta : T.cream, color: i === 0 ? "#FFF" : T.ink, border: i === 0 ? "none" : `1px solid ${T.line}`, padding: "9px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600 }}>
                  {i === 0 ? "● " : ""}
                  {n}
                </div>
              ))}
              <div style={{ color: T.muted, padding: "9px 14px", fontSize: 13, fontWeight: 500 }}>+ 30 andere plaatsen</div>
            </div>
            <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 20, display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{ flexShrink: 0, fontSize: 36, lineHeight: 1 }}>🏡</div>
              <div>
                <div style={{ fontSize: 14, color: T.ink, lineHeight: 1.55, fontStyle: "italic", marginBottom: 8 }}>&ldquo;{c.review.quote}&rdquo;</div>
                <div style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                  {c.review.name}{c.review.city ? ` · ${c.review.city}` : ""}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function _Journey({ content }: { content: RegioContent }) {
  const c = resolve(content);
  const steps = [
    { d: "Vandaag", lbl: "U vraagt brochure aan", sub: "30 seconden" },
    { d: "Binnen 5 werkdagen", lbl: "Adviseur bij u thuis", sub: "Gratis & vrijblijvend" },
    { d: "Binnen 4 weken", lbl: "Levering & installatie", sub: `In ${c.city}` },
  ];
  return (
    <section style={{ width: "100%", background: "#FAF6EA", padding: "60px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .rg-jrn-head { grid-template-columns: 1fr !important; gap: 20px !important; } .rg-jrn-h { font-size: 32px !important; } .rg-jrn-desktop { display: none !important; } .rg-jrn-mobile { display: block !important; } }@media (min-width: 1025px){ .rg-jrn-mobile { display: none !important; } }`}</style>
      <div className="rg-jrn-head" style={{ display: "grid", gridTemplateColumns: "1.05fr 1fr", gap: 48, alignItems: "flex-end", marginBottom: 44 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: T.cta, marginBottom: 12, textTransform: "uppercase" }}>Van ons naar u</div>
          <h2 className="rg-jrn-h" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 54, lineHeight: 1.05, color: T.ink, margin: 0, letterSpacing: "-0.02em" }}>
            De route naar uw
            <br />
            <span style={{ fontStyle: "italic", fontWeight: 400 }}>nieuwe badkamer</span>
          </h2>
        </div>
        <div style={{ fontSize: 16, color: T.inkSoft, lineHeight: 1.6, maxWidth: 440 }}>
          Een rechte lijn — geen tussenpersonen, geen verrassingen. Al <b style={{ color: T.ink }}>{c.projects.toLocaleString("nl-NL")} keer</b> afgelegd naar huizen in {c.city} en omgeving.
        </div>
      </div>
      <div className="rg-jrn-desktop" style={{ position: "relative", padding: "50px 0 20px" }}>
        <svg width="100%" height="6" style={{ position: "absolute", top: 72, left: 0, right: 0 }}>
          <line x1="8%" y1="3" x2="92%" y2="3" stroke={T.cta} strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" />
        </svg>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr 1fr auto", alignItems: "start", gap: 0, position: "relative" }}>
          <div style={{ textAlign: "center", padding: "0 16px" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.ink, color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 24 }}>🏭</div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", color: T.muted, textTransform: "uppercase" }}>Onze werkplaats</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginTop: 2 }}>Eigen team</div>
          </div>
          {steps.map((s, i) => (
            <div key={i} style={{ textAlign: "center", padding: "0 14px" }}>
              <div style={{ width: 42, height: 42, borderRadius: "50%", background: T.white, border: `2px solid ${T.cta}`, color: T.cta, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 500, boxShadow: "0 6px 14px -6px rgba(0,0,0,0.3)" }}>
                {i + 1}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: T.cta, textTransform: "uppercase", marginBottom: 4 }}>{s.d}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 2 }}>{s.lbl}</div>
              <div style={{ fontSize: 13, color: T.muted }}>{s.sub}</div>
            </div>
          ))}
          <div style={{ textAlign: "center", padding: "0 16px" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.cta, color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 22, boxShadow: "0 10px 24px -8px rgba(0,0,0,0.35)" }}>🏡</div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", color: T.muted, textTransform: "uppercase" }}>Uw huis in</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginTop: 2 }}>{c.city}</div>
          </div>
        </div>
      </div>
      <div className="rg-jrn-mobile" style={{ display: "none", position: "relative", padding: "8px 0", marginLeft: 10 }}>
        <div aria-hidden style={{ position: "absolute", left: 24, top: 12, bottom: 20, width: 2, background: `repeating-linear-gradient(to bottom, ${T.cta} 0 6px, transparent 6px 14px)` }} />
        {[
          { icon: "🏭", eyebrow: "Onze werkplaats", label: "Eigen team", sub: "Geen onderaannemers" },
          ...steps.map((s, i) => ({ icon: String(i + 1), eyebrow: s.d, label: s.lbl, sub: s.sub, isStep: true })),
          { icon: "🏡", eyebrow: `Uw huis in ${c.city}`, label: "Klaar om te gebruiken", sub: "" },
        ].map((n, i, arr) => (
          <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", position: "relative", paddingBottom: i === arr.length - 1 ? 0 : 18 }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: i === 0 ? T.ink : i === arr.length - 1 ? T.cta : T.white, border: (n as { isStep?: boolean }).isStep ? `2px solid ${T.cta}` : "none", color: i === 0 || i === arr.length - 1 ? "#FFF" : T.cta, display: "flex", alignItems: "center", justifyContent: "center", fontSize: (n as { isStep?: boolean }).isStep ? 16 : 20, fontFamily: (n as { isStep?: boolean }).isStep ? "'Fraunces', serif" : "inherit", fontWeight: 500, flexShrink: 0, zIndex: 1 }}>
              {n.icon}
            </div>
            <div style={{ paddingTop: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: T.cta, textTransform: "uppercase", marginBottom: 2 }}>{n.eyebrow}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{n.label}</div>
              {n.sub ? <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>{n.sub}</div> : null}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 40, background: T.white, border: `1px solid ${T.line}`, borderRadius: 14, padding: "22px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 500, color: T.ink, marginBottom: 2 }}>Start vandaag — eerste stap kost 30 seconden</div>
          <div style={{ fontSize: 13, color: T.muted }}>{c.projects.toLocaleString("nl-NL")}+ huizen gingen u voor in de regio</div>
        </div>
        <a href={c.cta_href} style={{ background: T.cta, color: "#FFF", textDecoration: "none", padding: "14px 22px", borderRadius: 999, fontSize: 15, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 10px 22px -10px rgba(0,0,0,0.3)", whiteSpace: "nowrap" }}>
          {c.cta_text} <span style={{ fontSize: 16 }}>→</span>
        </a>
      </div>
    </section>
  );
}

